// ClauseScoreboard.jsx — DAS-2977
// The clause scoreboard: which alert ingredients actually predict beating the
// market. Reads GET /clause_effectiveness (Clause_Effectiveness, written by
// the DAS-2950 weekly aggregation — Thursdays 04:35 — seeded retrospectively
// by DAS-2976, plus every TRACKED-but-unmeasured affirmative clause).
// Honesty first: rows under the n-floor are greyed out with no verdict —
// a beat rate over 2 alerts is a coin toss, not a measurement.
// Owner feedback 2026-09-09: clause number opens the clause editor.

import React, { useEffect, useState } from 'react';
import { Table, Spin, Alert, Tag, message } from 'antd';
import api from '../functions/api';
import { fetchClauses } from '../functions/alertClauseService';
import useFetchDashboardStructure from '../functions/FetchDashboardStructure';
import ClauseEditor from './ClauseEditor';

export const N_FLOOR = 15; // below this, no verdict is honest

// DAS-2977 feedback (3): the visual verdict. Deliberately coarse — three
// buckets from beat rate + edge, only above the n-floor. The thresholds are
// display grouping, not a trading rule.
export const verdictFor = (r) => {
  if (!r || r.n_matured < N_FLOOR || r.beat_pct == null || r.avg_edge_pct == null) return null;
  if (r.beat_pct >= 50 && r.avg_edge_pct >= 5) return 'strong';
  if (r.avg_edge_pct <= -5) return 'weak';
  return 'mixed';
};

// Clause texts are templates ("The PE of <YF_Data, PE, ticker> is …") —
// collapse the placeholders for reading.
export const cleanClauseText = (txt) =>
  (txt || '').replace(/<[^>]*>/g, '…').replace(/\s+/g, ' ').trim();

const ClauseScoreboard = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [editorClause, setEditorClause] = useState(null);
  const { dashboardStructure } = useFetchDashboardStructure(true, 'dashboard');

  const load = () => {
    api.get('/clause_effectiveness')
      .then(res => setRows(res.data?.clauses || []))
      .catch(err => setError(err?.response?.data?.error || err.message));
  };
  useEffect(() => { load(); }, []);

  // DAS-2977 feedback (2): clause number -> the real clause editor, on the
  // full clause record (the scoreboard row only carries id + text).
  const openEditor = async (clauseId) => {
    try {
      const all = await fetchClauses();
      const hit = (all || []).find(c => Number(c.ID) === Number(clauseId));
      if (hit) setEditorClause(hit);
      else message.info(`Clause #${clauseId} not found in the editor list.`);
    } catch (err) {
      message.error('Could not load the clause: ' + (err?.message || err));
    }
  };

  if (error) return <Alert type="error" message="Could not load the clause scoreboard" description={error} />;
  if (rows === null) return <Spin style={{ display: 'block', margin: '40px auto' }} />;

  // DAS-2977 feedback (1): every column sortable; nulls sink to the bottom.
  const num = (v) => (v == null ? -Infinity : Number(v));
  const columns = [
    {
      title: 'Clause',
      key: 'clause',
      sorter: (a, b) => a.clause_id - b.clause_id,
      render: (_, r) => (
        <span style={{ opacity: r.n_matured < N_FLOOR ? 0.45 : 1 }}>
          <a
            data-testid="das2977-clause-link"
            onClick={() => openEditor(r.clause_id)}
            style={{ marginRight: 6 }}
          >
            #{r.clause_id}
          </a>
          {cleanClauseText(r.clause_txt) || '(clause text missing)'}
        </span>
      ),
    },
    {
      title: 'Side', dataIndex: 'side', key: 'side', width: 70,
      sorter: (a, b) => String(a.side || '').localeCompare(String(b.side || '')),
      render: (v) => v || '',
    },
    {
      title: 'Matured', dataIndex: 'n_matured', key: 'n', width: 90,
      sorter: (a, b) => num(a.n_matured) - num(b.n_matured),
      defaultSortOrder: 'descend',
    },
    {
      // DAS-2977 feedback (2): on how many DIFFERENT tickers the numbers rest
      title: 'Tickers', dataIndex: 'n_tickers', key: 'tickers', width: 80,
      sorter: (a, b) => num(a.n_tickers) - num(b.n_tickers),
      render: (v, r) => (r.n_matured ? <span data-testid="das2977-tickers">{v ?? '—'}</span> : ''),
    },
    {
      title: 'Beat rate',
      key: 'beat',
      width: 130,
      sorter: (a, b) => num(a.beat_pct) - num(b.beat_pct),
      render: (_, r) => {
        if (!r.n_matured) return <Tag>no matured alerts yet</Tag>;
        if (r.n_matured < N_FLOOR) return <Tag>too little data</Tag>;
        return <b>{r.beat_pct != null ? `${r.beat_pct.toFixed(0)}%` : '—'}</b>;
      },
    },
    {
      title: 'Avg edge',
      key: 'edge',
      width: 100,
      sorter: (a, b) => num(a.avg_edge_pct) - num(b.avg_edge_pct),
      render: (_, r) => {
        if (r.n_matured < N_FLOOR || r.avg_edge_pct == null) return '';
        const v = r.avg_edge_pct;
        return (
          <span style={{ color: v >= 0 ? '#3b6d11' : '#a32d2d' }}>
            {v >= 0 ? '+' : ''}{v.toFixed(1)}%
          </span>
        );
      },
    },
    {
      // DAS-2977 feedback (3): the visual verdict — strong / mixed / weak
      title: 'Verdict',
      key: 'verdict',
      width: 90,
      sorter: (a, b) => {
        const rank = { strong: 2, mixed: 1, weak: 0 };
        const rv = (r) => (verdictFor(r) in rank ? rank[verdictFor(r)] : -1);
        return rv(a) - rv(b);
      },
      render: (_, r) => {
        const v = verdictFor(r);
        if (!v) return '';
        const color = v === 'strong' ? 'green' : v === 'weak' ? 'red' : 'default';
        return <Tag data-testid="das2977-verdict" color={color}>{v}</Tag>;
      },
    },
  ];

  return (
    <div data-testid="das2977-clause-scoreboard">
      <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6, marginBottom: 14 }}>
        Every alert is built from clauses. This scoreboard shows, per clause, how the alerts
        where it was TRUE at firing performed against the S&amp;P&nbsp;500 six months later
        (sell alerts measured direction-correctly). It updates itself every Thursday and grows
        as alerts mature — clauses marked "no matured alerts yet" are tracked (live capture
        since 03/09/2026) and will earn their first verdict over the coming months. Data mix:
        reconstructed history (DAS-2976) plus live capture. "Tickers" = on how many different
        stocks the numbers rest (50 alerts on 3 tickers ≠ 50 alerts on 40). Verdict: strong =
        beat ≥ 50% with edge ≥ +5%, weak = edge ≤ −5%, mixed otherwise — display grouping,
        not a trading rule. Columns sort on click. Click a clause number to open it in the
        clause editor. Re-weighting clauses stays a deliberate, separate decision — this
        screen only shows the evidence.
      </div>
      <Table
        dataSource={rows}
        columns={columns}
        rowKey={(r) => `${r.clause_id}-${r.side || 'unmeasured'}`}
        pagination={false}
        size="small"
      />
      {editorClause && (
        <ClauseEditor
          visible={!!editorClause}
          clause={editorClause}
          dashboardStructure={dashboardStructure}
          onClose={() => setEditorClause(null)}
          refreshClauses={load}
        />
      )}
    </div>
  );
};

export default ClauseScoreboard;
